import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, ExternalLink } from "lucide-react";

/**
 * Build the default subject + body for a reply based on the record type.
 */
export function buildDefaultEmail(kind, item) {
  const firstName = (item?.name || "").split(/\s+/)[0] || "";
  if (kind === "quote") {
    return {
      subject: `Re: Richiesta preventivo${item?.service ? ` — ${item.service}` : ""}`,
      body:
`Buongiorno${firstName ? ` ${firstName}` : ""},

grazie per averci contattato tramite il nostro sito.
Abbiamo ricevuto la sua richiesta e la contatteremo a breve per fissare un sopralluogo o approfondire i dettagli del preventivo.

Per qualsiasi informazione può rispondere direttamente a questa email.

Cordiali saluti,
Greenhouse Solutions SPA
Via Nino Bixio, 182 — Monopoli (BA)
+39 351 926 1622
www.greenhousesolutionsspa.com`,
    };
  }
  return {
    subject: `Re: Candidatura${item?.role ? ` — ${item.role}` : ""}`,
    body:
`Buongiorno${firstName ? ` ${firstName}` : ""},

grazie per l'interesse mostrato verso Greenhouse Solutions SPA e per averci inviato la sua candidatura${item?.role ? ` per la posizione di ${item.role}` : ""}.
Abbiamo preso in carico il suo profilo e la ricontatteremo a breve.

Cordiali saluti,
Greenhouse Solutions SPA
Via Nino Bixio, 182 — Monopoli (BA)
+39 351 926 1622
www.greenhousesolutionsspa.com`,
  };
}

/**
 * Build the compose URL for each supported provider.
 * - gmail:      https://mail.google.com/mail/?view=cm&fs=1&to=&su=&body=
 * - outlook:    https://outlook.office.com/mail/deeplink/compose?to=&subject=&body=
 * - aruba:      https://webmail.aruba.it/ (does NOT support prefill — we open the inbox)
 * - default:    mailto:...?subject=&body= (uses system default client, incl. iOS Mail, macOS Mail, Thunderbird, Outlook desktop, Aruba PWA...)
 */
function buildProviderUrl(provider, { email, subject, body }) {
  const s = encodeURIComponent(subject || "");
  const b = encodeURIComponent(body || "");
  const to = encodeURIComponent(email || "");
  switch (provider) {
    case "gmail":
      return `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${s}&body=${b}`;
    case "outlook":
      return `https://outlook.office.com/mail/deeplink/compose?to=${to}&subject=${s}&body=${b}`;
    case "aruba":
      return `https://webmail.aruba.it/`;
    case "default":
    default:
      return `mailto:${email || ""}?subject=${s}&body=${b}`;
  }
}

const PROVIDERS = [
  { id: "default", label: "Client di sistema", note: "Mail iOS, macOS Mail, Thunderbird, Outlook desktop…", testid: "reply-provider-default" },
  { id: "gmail", label: "Gmail", note: "Apri Gmail Web nel browser", testid: "reply-provider-gmail" },
  { id: "outlook", label: "Outlook", note: "Apri Outlook Web nel browser", testid: "reply-provider-outlook" },
  { id: "aruba", label: "Aruba Webmail", note: "Apri Aruba Webmail (compose manuale)", testid: "reply-provider-aruba" },
];

export default function ReplyDialog({ open, onOpenChange, email, kind, item }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  // Reset fields whenever the dialog opens on a new item.
  useEffect(() => {
    if (open) {
      const d = buildDefaultEmail(kind, item);
      setSubject(d.subject);
      setBody(d.body);
    }
  }, [open, kind, item]);

  const handleSend = (provider) => {
    const url = buildProviderUrl(provider, { email, subject, body });
    if (provider === "default") {
      // mailto: hands off to the OS
      window.location.href = url;
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="reply-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-black text-2xl tracking-tightest flex items-center gap-3">
            <Mail className="w-6 h-6 text-primary" />
            Rispondi a {item?.name || email}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-1.5">
            <Label className="font-mono text-xs uppercase tracking-widest">Destinatario</Label>
            <Input
              value={email || ""}
              readOnly
              className="rounded-none font-mono text-sm bg-muted/40"
              data-testid="reply-to"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="font-mono text-xs uppercase tracking-widest">Oggetto</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="rounded-none"
              data-testid="reply-subject"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="font-mono text-xs uppercase tracking-widest">Messaggio</Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={12}
              className="rounded-none font-mono text-sm leading-relaxed"
              data-testid="reply-body"
            />
            <p className="font-mono text-[10px] text-muted-foreground">
              Modifica liberamente oggetto e messaggio prima di inviare. Scegli poi il provider con cui aprire la mail.
            </p>
          </div>

          <div className="pt-2">
            <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-3">// Apri con</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PROVIDERS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => handleSend(p.id)}
                  data-testid={p.testid}
                  className="group flex items-center justify-between gap-3 bg-card p-4 hover:bg-primary hover:text-primary-foreground transition-colors text-left"
                >
                  <div className="min-w-0">
                    <p className="font-head font-bold text-base tracking-tight">{p.label}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground group-hover:text-primary-foreground/80 mt-1 truncate">
                      {p.note}
                    </p>
                  </div>
                  <ExternalLink className="w-4 h-4 shrink-0 opacity-70" />
                </button>
              ))}
            </div>
            <p className="font-mono text-[9px] text-muted-foreground mt-3 leading-relaxed">
              Nota: Aruba Webmail non supporta il pre-riempimento via URL. Con Aruba dovrai incollare manualmente destinatario e testo dopo aver cliccato &quot;Nuovo Messaggio&quot;.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
